import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Alert, Box, Button, CircularProgress, Grid, LinearProgress, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useStore } from 'src/stores';
import { exportCSVFromDataGrid } from 'src/utils/exportCSV';
import { AUTO_HEIGHT_SCROLLBAR_ROOM } from 'src/utils/dataGrid';
import { GRAINS, automatedShare, isAutomatedUnknown, pivotCoverage } from './pivot';
import { SourceChip } from './parts';
import { useAsyncList } from './useAsyncList';
const AUTOMATED = '#2e7d32';
const MANUAL = '#d32f2f';

/** How many bars fit before the labels stop being readable. The rest stay in the table below. */
const CHART_ROWS = 12;

/**
 * Manual versus automated, at whichever grain the reader works in.
 *
 * One payload, three folds. A per-provider grid answers "what should we build next"; it cannot
 * answer "which customer is still doing this by hand", which is the question this tab exists for.
 */
export const CustomersView = observer(() => {
  const store = useStore();
  const state = useAsyncList(store.admin.getBillScrapingCoverageAsync, () => store.admin.getBillScrapingCoverageAsync.run(), 'Could not load coverage.', [store]);
  const _useState = useState('company'),
    _useState2 = _slicedToArray(_useState, 2),
    grain = _useState2[0],
    setGrain = _useState2[1];
  const totals = useMemo(() => automatedShare(state.rows), [state.rows]);
  const pivoted = useMemo(() => pivotCoverage(state.rows, grain), [state.rows, grain]);
  // Not reversed: a vertical Recharts axis renders index 0 at the top, so the sort order already
  // puts the largest manual workload where the eye lands first. Measured, not assumed.
  const charted = useMemo(() => pivoted.slice(0, CHART_ROWS), [pivoted]);
  const columns = useMemo(() => [{
    field: 'name',
    headerName: GRAINS[grain].label,
    flex: 1.4,
    minWidth: 200,
    isExportable: true
  }, {
    field: 'utilities',
    headerName: 'Utilities',
    width: 110,
    type: 'number',
    isExportable: true
  }, {
    field: 'automated',
    headerName: 'Automated',
    width: 120,
    type: 'number',
    isExportable: true
  }, {
    field: 'manual',
    headerName: 'By hand',
    width: 110,
    type: 'number',
    isExportable: true
  }, {
    field: 'wired',
    headerName: 'Wired up',
    width: 200,
    isExportable: true,
    // Wired but not automated is the useful cell: the scraper exists and the chain still breaks.
    // notProven is clamped per source row upstream, never derived from these folded totals.
    renderCell: _ref => {
      let row = _ref.row;
      return /*#__PURE__*/React.createElement(Typography, {
        variant: "body2"
      }, row.wired, row.notProven ? /*#__PURE__*/React.createElement(Typography, {
        component: "span",
        variant: "caption",
        color: "text.secondary"
      }, " (".concat(row.notProven, " not proven end to end)")) : null);
    },
    // Mirrors the cell, which omits the suffix entirely when nothing is left to prove.
    valueGetterForExport: _ref2 => {
      let row = _ref2.row;
      return row.notProven ? "".concat(row.wired, " (").concat(row.notProven, " not proven end to end)") : "".concat(row.wired);
    }
  }, {
    field: 'percent',
    headerName: 'Automated %',
    width: 150,
    isExportable: true,
    type: 'number',
    renderCell: _ref3 => {
      let row = _ref3.row;
      return row.percent == null ? /*#__PURE__*/React.createElement(Typography, {
        variant: "caption"
      }, "Unknown") : /*#__PURE__*/React.createElement(Stack, {
        sx: {
          width: '100%',
          paddingY: '6px'
        }
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "caption"
      }, "".concat(row.percent, "%")), /*#__PURE__*/React.createElement(LinearProgress, {
        variant: "determinate",
        value: row.percent,
        sx: {
          height: 6,
          borderRadius: 3
        }
      }));
    },
    valueGetterForExport: _ref4 => {
      let row = _ref4.row;
      return row.percent == null ? 'Unknown' : "".concat(row.percent, "%");
    }
  }], [grain]);
  if (state.loading) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        padding: '32px',
        textAlign: 'center'
      }
    }, /*#__PURE__*/React.createElement(CircularProgress, null));
  }
  if (state.error) {
    return /*#__PURE__*/React.createElement(Alert, {
      severity: "error",
      sx: {
        marginTop: '16px'
      }
    }, state.error);
  }
  const unknown = isAutomatedUnknown(state.rows);
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      marginTop: '16px'
    }
  }, unknown ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    sx: {
      marginBottom: '16px'
    }
  }, "Could not check how bills arrived, so the automated counts below are not known right now.") : null, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 16,
    sx: {
      marginBottom: '16px'
    }
  }, [{
    label: 'Utilities billed by Livable',
    value: totals.utilities
  }, {
    label: 'Automated end to end',
    value: unknown ? 'Unknown' : totals.automated,
    literal: unknown
  }, {
    label: 'Still done by hand',
    value: unknown ? 'Unknown' : Math.max(0, totals.utilities - totals.automated),
    literal: unknown
  }, {
    label: 'Automated',
    value: unknown ? 'Unknown' : "".concat(totals.percent, "%"),
    literal: true
  }].map(tile => /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 3,
    key: tile.label
  }, /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, tile.label), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, tile.literal ? tile.value : tile.value.toLocaleString()))))), /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px',
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 16,
    alignItems: "center",
    flexWrap: "wrap",
    useFlexGap: true,
    sx: {
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement(ToggleButtonGroup, {
    exclusive: true,
    size: "small",
    value: grain,
    onChange: (_, value) => value && setGrain(value),
    "aria-label": "coverage grain"
  }, Object.entries(GRAINS).map(_ref5 => {
    let _ref6 = _slicedToArray(_ref5, 2),
      value = _ref6[0],
      spec = _ref6[1];
    return (
      /*#__PURE__*/
      // disableRipple: an inverting dark-mode extension paints MUI's TouchRipple opaque over
      // the label, and this control is how the tab is navigated. Same reason LivyTheme
      // disables it for its subtree.
      React.createElement(ToggleButton, {
        disableRipple: true,
        key: value,
        value: value
      }, spec.label)
    );
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "Automated: a portal scrape or an email brought the bill in and it reached the allocation table without anyone typing it. Anything else is by hand."), /*#__PURE__*/React.createElement(Box, {
    sx: {
      flexGrow: 1
    }
  }), /*#__PURE__*/React.createElement(SourceChip, null), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => exportCSVFromDataGrid({
      columns,
      rows: pivoted,
      name: "coverage-by-".concat(grain, ".csv")
    })
  }, "Export CSV")), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: '100%',
      height: Math.max(240, charted.length * 28 + 60),
      display: unknown ? 'none' : 'block'
    }
  }, /*#__PURE__*/React.createElement(ResponsiveContainer, null, /*#__PURE__*/React.createElement(BarChart, {
    data: charted,
    layout: "vertical",
    margin: {
      left: 24
    }
  }, /*#__PURE__*/React.createElement(CartesianGrid, {
    strokeDasharray: "3 3"
  }), /*#__PURE__*/React.createElement(XAxis, {
    type: "number"
  }), /*#__PURE__*/React.createElement(YAxis, {
    type: "category",
    dataKey: "name",
    width: 200,
    interval: 0,
    tick: {
      fontSize: 12
    }
  }), /*#__PURE__*/React.createElement(Tooltip, null), /*#__PURE__*/React.createElement(Legend, null), /*#__PURE__*/React.createElement(Bar, {
    stackId: "u",
    dataKey: "automated",
    name: "Automated",
    fill: AUTOMATED,
    isAnimationActive: false
  }), /*#__PURE__*/React.createElement(Bar, {
    stackId: "u",
    dataKey: "manual",
    name: "By hand",
    fill: MANUAL,
    isAnimationActive: false
  })))), pivoted.length > CHART_ROWS ? /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "Top ".concat(CHART_ROWS, " by manual volume. All ").concat(pivoted.length, " are in the table below.")) : null), /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px'
    }
  }, /*#__PURE__*/React.createElement(DataGrid, {
    autoHeight: true,
    sx: AUTO_HEIGHT_SCROLLBAR_ROOM,
    columns: columns,
    rows: pivoted,
    getRowId: row => row.key,
    getRowHeight: () => 'auto',
    disableRowSelectionOnClick: true,
    initialState: {
      pagination: {
        paginationModel: {
          page: 0,
          pageSize: 25
        }
      }
    },
    pageSizeOptions: [25, 50, 100]
  })));
});